/**
 * ForwardModal
 * Seleziona una chat a cui inoltrare il messaggio
 */

import { useState, useEffect } from 'react';
import { X, Search, Forward, Loader2 } from 'lucide-react';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';

interface ForwardModalProps {
  messageId: string;
  onClose: () => void;
}

export function ForwardModal({ messageId, onClose }: ForwardModalProps) {
  const [query, setQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const { chats, allChatsCache, loadAllChatsCache, forwardMessageAction } = useWhatsAppChatStore();

  useEffect(() => {
    if (!allChatsCache) loadAllChatsCache();
  }, [allChatsCache, loadAllChatsCache]);

  const candidates = (allChatsCache || chats).filter((c) =>
    c.name.toLowerCase().includes(query.toLowerCase())
  ).slice(0, 30);

  const handleSend = async (toChatId: string) => {
    setSending(true);
    try {
      await forwardMessageAction(messageId, toChatId);
      setDone(true);
      setTimeout(onClose, 1200);
    } catch {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-[rgba(55,53,47,0.09)] dark:border-white/10 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
          <Forward className="w-5 h-5 text-[#787774]" />
          <h2 className="flex-1 text-sm font-semibold text-[#37352F] dark:text-white">Inoltra messaggio</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10">
            <X className="w-4 h-4 text-[#787774]" />
          </button>
        </div>

        {done ? (
          <div className="flex items-center justify-center py-8 text-sm text-green-600 dark:text-green-400 gap-2">
            <Forward className="w-5 h-5" /> Messaggio inoltrato!
          </div>
        ) : (
          <>
            {/* Search */}
            <div className="px-4 py-3 border-b border-[rgba(55,53,47,0.06)] dark:border-white/8">
              <div className="flex items-center gap-2 px-3 py-2 bg-[rgba(55,53,47,0.06)] dark:bg-white/8 rounded-lg">
                <Search className="w-4 h-4 text-[#787774] flex-shrink-0" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cerca chat..."
                  className="flex-1 text-sm bg-transparent outline-none text-[#37352F] dark:text-white placeholder:text-[#787774]"
                />
              </div>
            </div>

            {/* Chat list */}
            <div className="max-h-64 overflow-y-auto">
              {candidates.length === 0 && (
                <p className="text-center text-xs text-[#787774] py-6">Nessuna chat trovata</p>
              )}
              {candidates.map((chat) => (
                <button
                  key={chat.id}
                  onClick={() => handleSend(chat.id)}
                  disabled={sending}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/6 transition-colors text-left disabled:opacity-50"
                >
                  <div className="w-8 h-8 rounded-full bg-[rgba(55,53,47,0.12)] dark:bg-white/15 flex items-center justify-center text-sm font-medium text-[#37352F] dark:text-white flex-shrink-0">
                    {chat.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm text-[#37352F] dark:text-white truncate">{chat.name}</span>
                  {sending && <Loader2 className="w-4 h-4 animate-spin ml-auto text-[#787774]" />}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
